import { AlertColor, Box, Chip, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { privileges } from '../../../../utils/constants/privileges';
import { identifiers } from '../../../../utils/constants/identifiers';
import { URLS } from '../../../../utils/constants/urls';
import { filterDataforApi, pageNo, rowCount } from '../../../../utils/redux/reducer/filter-slice';
import { Privilege, selectedOrgId } from '../../../../utils/redux/reducer/authentication-slice';
import { fetchFromStorage } from '../../../../utils/storage';
import { getListData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { usageTableColumn } from '../../CommonPage/usageColumn';
import { AddEditPartOfDayForm, PART_OF_DAY_SCOPE_ORGANIZATION, PART_OF_DAY_SCOPE_PARENT } from './AddEditPartOfDay/AddEditPartOfDayForm';
import PartOfDayColorPicker, { isValidHexColor, normalizeHexColor } from './PartOfDayColorPicker';
import { PageProps } from '../../../../utils/constants/data';

const DEFAULT_COLOR = '#66BB6A';

const resolveStoredOrganizationId = (): string | number | undefined => {
  const initOrg = fetchFromStorage(identifiers.organization) as any;
  if (!initOrg || initOrg === '*') {
    return undefined;
  }
  try {
    const parsed = typeof initOrg === 'string' ? JSON.parse(initOrg) : initOrg;
    return parsed?.value ?? parsed?.id;
  } catch {
    return undefined;
  }
};

const PartOfDayList: FC = () => {
  const { id: routeOrgId } = useParams() as { id?: string };
  const selectedOrganization = useSelector(selectedOrgId) as { value?: string | number } | null;
  const userPrivileges = useSelector(Privilege) as string[];
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);

  const orgId = useMemo(
    () => routeOrgId ?? selectedOrganization?.value ?? resolveStoredOrganizationId(),
    [routeOrgId, selectedOrganization],
  );
  const isParent = false;

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [active, setActive] = useState(true);
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
    });
  };

  const resolveScope = (data: { orgId?: number | null; inherited?: boolean }) => {
    if (data.inherited || data.orgId == null) {
      return PART_OF_DAY_SCOPE_PARENT;
    }
    return PART_OF_DAY_SCOPE_ORGANIZATION;
  };

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      if (!orgId) {
        return;
      }
      setEditingId(recordId);
      if (recordId) {
        try {
          setDrawerLoading(true);
          const { data } = await axiosInstance.get(URLS.partOfDayById(recordId, orgId, isParent));
          setFormValues({
            code: data.code,
            name: data.name,
            scope: resolveScope(data),
            sortOrder: data.sortOrder,
          });
          setColor(data.color || DEFAULT_COLOR);
          setActive(Boolean(data.active));
        } catch (error: any) {
          handleCatch(error);
          return;
        } finally {
          setDrawerLoading(false);
        }
      } else {
        setFormValues({
          code: '',
          name: '',
          scope: PART_OF_DAY_SCOPE_ORGANIZATION,
          sortOrder: 1,
        });
        setColor(DEFAULT_COLOR);
        setActive(true);
      }
      setDrawerOpen(true);
    },
    [orgId, isParent],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const listApi = useMemo(() => (orgId ? URLS.partOfDayList(orgId, isParent) : ''), [orgId, isParent]);

  const getList = useCallback(
    (filterData: PageProps) => {
      if (!orgId) {
        return;
      }
      const body = filterData ? filterData : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['sortOrder:asc'] };
      getListData(setLoading, body, listApi, setPageResponse);
    },
    [listApi, index, pageSize, filter, orgId],
  );

  useEffect(() => {
    getList(null as any);
  }, [getList]);

  const handleSave = async (body: FormDataModel) => {
    if (!orgId) {
      return;
    }
    const colorValue = String(color ?? '').trim();
    if (!isValidHexColor(colorValue)) {
      setMessage({ display: true, severity: 'error', message: strings.partOfDay_color_invalid });
      return;
    }
    const normalizedColor = normalizeHexColor(colorValue);
    const isParentScope = body.scope === PART_OF_DAY_SCOPE_PARENT;
    try {
      setSaving(true);
      if (editingId) {
        const updatePayload = {
          name: String(body.name ?? '').trim(),
          color: normalizedColor,
          sortOrder: Number(body.sortOrder ?? 0),
          active: Boolean(active),
        };
        await axiosInstance.put(URLS.partOfDayUpdate(orgId, editingId, isParentScope), updatePayload);
      } else {
        const createPayload = {
          code: String(body.code ?? '').trim().toUpperCase(),
          name: String(body.name ?? '').trim(),
          color: normalizedColor,
          sortOrder: Number(body.sortOrder ?? 0),
          active: Boolean(active),
        };
        await axiosInstance.post(URLS.partOfDayCreate(orgId, isParentScope), createPayload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId || !orgId) {
      return;
    }
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.partOfDayDelete(editingId, orgId, isParent));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.partOfDay_title_list,
      subtitle: strings.partOfDay_list_subtitle,
      searchField: 'name',
      searchPlaceholder: 'Search part of day',
      AddButtonText: strings.partOfDay_button_add,
      listApi,
      addPrivilege: privileges.add_part_of_day,
      editPrivilege: privileges.edit_part_of_day,
      deletePrivilege: privileges.delete_part_of_day,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      tableColumn: [
        { field: 'code', headerName: strings.partOfDay_column_code, flex: 1, sortable: true },
        { field: 'name', headerName: strings.partOfDay_column_name, flex: 1, sortable: true },
        {
          field: 'inherited',
          headerName: strings.partOfDay_column_scope,
          flex: 0.8,
          sortable: false,
          renderCell: (_value: any, row: any) => (
            <Chip
              size="small"
              label={row.inherited ? strings.partOfDay_scope_parent : strings.partOfDay_scope_organization}
              color={row.inherited ? 'default' : 'primary'}
              variant={row.inherited ? 'outlined' : 'filled'}
            />
          ),
        },
        {
          field: 'color',
          headerName: strings.partOfDay_column_color,
          flex: 1,
          sortable: false,
          renderCell: (_value: any, row: any) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: row.color, border: '1px solid #ccc' }} />
              <Typography variant="body2">{row.color}</Typography>
            </Box>
          ),
        },
        { field: 'sortOrder', headerName: strings.partOfDay_column_sort_order, flex: 0.7, sortable: true },
        {
          field: 'active',
          headerName: strings.partOfDay_column_active,
          flex: 0.7,
          sortable: true,
          renderCell: (_value: any, row: any) => (
            <Chip
              size="small"
              label={row.active ? strings.dropdown_yes_text : strings.dropdown_no_text}
              color={row.active ? 'success' : 'default'}
            />
          ),
        },
        usageTableColumn(row => [{ count: row.bookingCount, singular: 'Booking', plural: 'Bookings' }]),
        lastUpdatedTableColumn(),
      ],
    }),
    [listApi, openDrawer],
  );

  if (!orgId) {
    return (
      <Typography variant="body1" sx={{ mt: 2 }}>
        {strings.no_data_found}
      </Typography>
    );
  }

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <ListPage isLoading={loading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.partOfDay_title_edit : strings.partOfDay_title_add}
        isEdit={Boolean(editingId)}
        formModel={AddEditPartOfDayForm(strings, 12, Boolean(editingId), true)}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_part_of_day)}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}>
        <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5, mb: 1 }}>
          <Grid item xs={12} md={8}>
            <PartOfDayColorPicker value={color} onChange={setColor} />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              sx={{ m: 0, width: '100%', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
              control={<Switch checked={active} onChange={event => setActive(event.target.checked)} color="primary" />}
              label={strings.partOfDay_field_active}
            />
          </Grid>
        </Grid>
      </MasterDataEntityDrawer>
    </>
  );
};

export default PartOfDayList;
